import type { PayloadAction } from "@reduxjs/toolkit";
import { createSlice } from "@reduxjs/toolkit";
import { persistReducer } from "redux-persist";
import storage from "redux-persist/lib/storage"; // defaults to localStorage for web
import type { AppState } from "./types";
import type { IframeSource, ThemeMode } from "@/types/iframe-messages";
import type { PaletteOptions } from "@mui/material/styles";

const initialState: AppState = {
  initialized: false,
  brandId: null,
  brandCityId: null,
  pointId: null,
  maxPreorderAmount: null,
  isReadOnly: false,
  debug: false,
  pendingEditOrderId: null,
  source: "delivery",
  isIframe: false,
  parentReady: false,
  capabilities: ["ordering"],
  theme: "light",
  customColors: undefined,
};

export const appSlice = createSlice({
  name: "app",
  initialState,
  reducers: {
    initApp: (state) => {
      state.initialized = true;
    },
    setBrandId: (state, action: PayloadAction<string | null>) => {
      state.brandId = action.payload;
    },
    setBrandCityId: (state, action: PayloadAction<string | null>) => {
      state.brandCityId = action.payload;
    },
    setPointId: (state, action: PayloadAction<string | null>) => {
      state.pointId = action.payload;
    },
    setMaxPreorderAmount: (state, action: PayloadAction<number | null>) => {
      state.maxPreorderAmount = action.payload;
    },
    setReadOnly: (state, action: PayloadAction<boolean>) => {
      state.isReadOnly = action.payload;
    },
    setDebug: (state, action: PayloadAction<boolean>) => {
      state.debug = action.payload;
    },
    setPendingEditOrderId: (state, action: PayloadAction<string | null>) => {
      state.pendingEditOrderId = action.payload;
    },
    resetApp: (state) => {
      state.initialized = false;
      state.brandId = null;
      state.brandCityId = null;
      state.pointId = null;
      state.maxPreorderAmount = null;
      state.isReadOnly = false;
      state.debug = false;
      state.pendingEditOrderId = null;
      state.parentReady = false;
      state.capabilities = ["ordering"];
      state.theme = "light";
      state.customColors = undefined;
    },
    setSource: (state, action: PayloadAction<IframeSource>) => {
      state.source = action.payload;
    },
    setIframeMode: (state, action: PayloadAction<boolean>) => {
      state.isIframe = action.payload;
    },
    setParentReady: (state, action: PayloadAction<boolean>) => {
      state.parentReady = action.payload;
    },
    setCapabilities: (state, action: PayloadAction<string[]>) => {
      state.capabilities = action.payload;
    },
    setTheme: (state, action: PayloadAction<ThemeMode>) => {
      state.theme = action.payload;
    },
    setCustomColors: (
      state,
      action: PayloadAction<PaletteOptions | undefined>,
    ) => {
      state.customColors = action.payload;
    },
    initializeApp: (
      state,
      action: PayloadAction<{
        source: IframeSource;
        isIframe: boolean;
        capabilities: string[];
      }>,
    ) => {
      state.source = action.payload.source;
      state.isIframe = action.payload.isIframe;
      state.capabilities = action.payload.capabilities;
      state.initialized = true;
    },
  },
});

export const {
  initApp,
  setBrandId,
  setBrandCityId,
  setPointId,
  setMaxPreorderAmount,
  setReadOnly,
  setDebug,
  setPendingEditOrderId,
  resetApp,
  setSource,
  setIframeMode,
  setParentReady,
  setCapabilities,
  setTheme,
  setCustomColors,
  initializeApp,
} = appSlice.actions;

// Configuration for Redux persist using localStorage
const persistConfig = {
  key: "app",
  storage,
  whitelist: ["brandId", "pointId", "brandCityId", "source", "theme"],
};

export default persistReducer(persistConfig, appSlice.reducer);
